import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import { Save, Loader2, Clock, Bell, ShieldCheck, Sun, Moon } from 'lucide-react';
import styles from './AttendanceSettings.module.css';

const AttendanceSettings = () => {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        office_start_time: '',
        office_end_time: '',
        grace_minutes: 0,
        first_half_end_time: '',
        second_half_start_time: '',
        punchin_reminder_1: '',
        punchin_reminder_2: '',
        punchin_final_warning: '',
        punchout_reminder: '',
        is_active: true
    });

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const parts = timeStr.split(':');
        if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
        return timeStr;
    };

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/attendance-settings');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setFormData({
                    office_start_time: formatTime(data.office_start_time) || '',
                    office_end_time: formatTime(data.office_end_time) || '',
                    grace_minutes: data.grace_minutes || 0,
                    first_half_end_time: formatTime(data.first_half_end_time) || '',
                    second_half_start_time: formatTime(data.second_half_start_time) || '',
                    punchin_reminder_1: formatTime(data.punchin_reminder_1) || '',
                    punchin_reminder_2: formatTime(data.punchin_reminder_2) || '',
                    punchin_final_warning: formatTime(data.punchin_final_warning) || '',
                    punchout_reminder: formatTime(data.punchout_reminder) || '',
                    is_active: data.is_active ?? true
                });
            }
        } catch (error) {
            console.error('Error fetching attendance settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const response = await api.put('/attendance-settings', formData);
            if (response.data.success) {
                toast.success('Settings updated successfully');
            } else {
                toast.error(response.data.message || 'Failed to update settings');
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error(error.response?.data?.message || 'Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles['attendance-form-layout']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={toggleSidebar} />
                
                <main className={styles['main-content']}>
                    <Breadcrumbs customItems={[
                        { label: 'HR', path: '#' },
                        { label: 'Attendance Settings', path: '/employees/attendance-settings' }
                    ]} />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title-section']}>
                            <h1>Attendance Settings</h1>
                            <p>Configure office timings, grace periods, and reminders globally.</p>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                            <Loader2 size={40} className="spinner" style={{ color: '#f97316' }} />
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className={styles['form-container']}>
                                <div className={styles['left-column']}>
                                    
                                    {/* Office Timings */}
                                    <div className={styles['form-card']}>
                                        <div className={styles['card-header']}>
                                            <Clock size={20} color="#f97316" />
                                            <h3>Primary Office Timings</h3>
                                        </div>
                                        <div className={styles['form-grid']}>
                                            <div className={styles['form-group']}>
                                                <label><Clock size={14} /> Start Time *</label>
                                                <input
                                                    type="time" name="office_start_time" required
                                                    className={styles['form-input']}
                                                    value={formData.office_start_time} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Clock size={14} /> End Time *</label>
                                                <input
                                                    type="time" name="office_end_time" required
                                                    className={styles['form-input']}
                                                    value={formData.office_end_time} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Clock size={14} /> Grace Period (Mins) *</label>
                                                <input
                                                    type="number" name="grace_minutes" required min="0"
                                                    className={styles['form-input']} placeholder="e.g. 15"
                                                    value={formData.grace_minutes} onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Half Day Timings */}
                                    <div className={styles['form-card']}>
                                        <div className={styles['card-header']}>
                                            <Sun size={20} color="#3b82f6" />
                                            <h3>Half-Day Boundaries</h3>
                                        </div>
                                        <div className={styles['form-grid']}>
                                            <div className={styles['form-group']}>
                                                <label><Clock size={14} /> First Half End Time</label>
                                                <input
                                                    type="time" name="first_half_end_time"
                                                    className={styles['form-input']}
                                                    value={formData.first_half_end_time} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Clock size={14} /> Second Half Start Time</label>
                                                <input
                                                    type="time" name="second_half_start_time"
                                                    className={styles['form-input']}
                                                    value={formData.second_half_start_time} onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reminders */}
                                    <div className={styles['form-card']}>
                                        <div className={styles['card-header']}>
                                            <Bell size={20} color="#6366f1" />
                                            <h3>Automated Reminders</h3>
                                        </div>
                                        <div className={styles['form-grid']}>
                                            <div className={styles['form-group']}>
                                                <label><Bell size={14} /> Punch-in Reminder 1</label>
                                                <input
                                                    type="time" name="punchin_reminder_1"
                                                    className={styles['form-input']}
                                                    value={formData.punchin_reminder_1} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Bell size={14} /> Punch-in Reminder 2</label>
                                                <input
                                                    type="time" name="punchin_reminder_2"
                                                    className={styles['form-input']}
                                                    value={formData.punchin_reminder_2} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Bell size={14} /> Punch-in Final Warning</label>
                                                <input
                                                    type="time" name="punchin_final_warning"
                                                    className={styles['form-input']}
                                                    value={formData.punchin_final_warning} onChange={handleChange}
                                                />
                                            </div>
                                            <div className={styles['form-group']}>
                                                <label><Bell size={14} /> Punch-out Reminder</label>
                                                <input
                                                    type="time" name="punchout_reminder"
                                                    className={styles['form-input']}
                                                    value={formData.punchout_reminder} onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className={styles['right-column']}>
                                    {/* Tracking Configuration */}
                                    <div className={styles['form-card']}>
                                        <div className={styles['card-header']}>
                                            <ShieldCheck size={20} color="#10b981" />
                                            <h3>Tracking Configuration</h3>
                                        </div>
                                        <div className={styles['toggle-grid']}>
                                            <div className={styles['toggle-group']}>
                                                <div className={styles['toggle-label']}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <ShieldCheck size={16} color="#0f172a" />
                                                        <span>Enable Tracking</span>
                                                    </div>
                                                    <span>Activate global attendance tracking</span>
                                                </div>
                                                <label className={styles['switch']}>
                                                    <input
                                                        type="checkbox" name="is_active"
                                                        checked={formData.is_active} onChange={handleChange}
                                                    />
                                                    <span className={styles['slider']}></span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles['footer-actions']}>
                                <button type="button" className={styles['btn-cancel']} onClick={() => navigate(-1)}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles['btn-save']} disabled={saving}>
                                    {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
                                    <span>Save Settings</span>
                                </button>
                            </div>
                        </form>
                    )}
                    
                    <Footer />
                </main>
            </div>
        </div>
    );
};

export default AttendanceSettings;
